<template>
  <vue-good-table 
    styleClass="vgt-table condensed"
    v-bind="tableOptions"
    :columns="columns"
    :rows="rows"
    :search-options="activeSearchOptions"
    :pagination-options="paginationFor(rows)"
    :sort-options="sortOptions"
    :row-style-class="rowStyleClass"
    :row-click="true"
    @row-click="onRowClick"
    compactMode>
    <template #table-row="props">
      <div v-if="props.column.field === 'UserNickName'" class="d-flex align-items-center justify-content-sm-start justify-content-end">
        <div>
          <a 
            :title="props.row.UserNickName" 
            :data-bs-original-title="props.row.UserNickName" 
            href="javascript:;" 
            data-bs-toggle="tooltip" 
            data-bs-placement="bottom">
              <img 
              :src="userAvatarUrl(props.row.UserImage, store.state.imageCacheKey, true)" 
              class="avatar avatar-xs me-2 result-avatar"
              alt="player">
          </a>
        </div>
        <button
          type="button"
          class="user-info-btn"
          :title="'Profil hráče ' + props.row.UserNickName"
          @click.stop.prevent="goToUserPage(props.row)"
        >i</button>
        <div class="d-flex align-items-center">
          <h6
            class="mb-0 text-sm"
            :class="{
              'text-primary': isOwnPlayer(props.row.UserID) || (isFocusedPlayer(props.row.UserID) && !hasOwnPlayer),
              'text-player-viewed': isViewedOther(props.row.UserID)
            }"
          >
            {{ props.row.UserNickName }}
          </h6>
        </div>
      </div> 
      <span v-else>{{ props.formattedRow[props.column.field] }}</span>
    </template>
    <template #emptystate>
      <div class="text-center">
        <span>Žádná data</span>
      </div>
    </template>
  </vue-good-table>
</template>

<script setup>
import { ref, reactive, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { tableOptions, tableSearchOptions, paginationFor } from '../../../../helpers/tableOptions';
import { userAvatarUrl } from '@/helpers/userAvatars';

const props = defineProps({
  dataLoading: Boolean,
  searchable: {
    type: Boolean,
    default: true
  },
  rows: {
    type: [Array, Object]
  },
  highlightedUserId: {
    default: null
  },
  ownUserId: {
    default: undefined
  }
})
const emit = defineEmits(['result-clicked']);
const store = useStore();
const router = useRouter();

const activeUserId = ref(null);
const sameUser = (a, b) => {
  const left = Number(a);
  const right = Number(b);
  return Number.isFinite(left) && left === right;
};
const resolvedOwnUserId = computed(() => (
  props.ownUserId === undefined ? store.state.user?.UserID : props.ownUserId
));
const hasOwnPlayer = computed(() => resolvedOwnUserId.value != null && resolvedOwnUserId.value !== '');
const isOwnPlayer = (id) => sameUser(id, resolvedOwnUserId.value);
const isFocusedPlayer = (id) => (
  sameUser(id, props.highlightedUserId) || sameUser(id, activeUserId.value)
);
const isViewedOther = (id) => hasOwnPlayer.value && isFocusedPlayer(id) && !isOwnPlayer(id);

watch(() => props.highlightedUserId, (id) => {
  if (id != null && id !== '') activeUserId.value = id;
}, { immediate: true });

const activeSearchOptions = computed(() => props.searchable
  ? { ...tableSearchOptions, placeholder: 'Hledat hráče…' }
  : { enabled: false }
);
const sortOptions = {
  enabled: true,
  initialSortBy: { field: 'Rank', type: 'asc' }
};
const rowStyleClass = (row) => {
  if (isOwnPlayer(row.UserID) || (isFocusedPlayer(row.UserID) && !hasOwnPlayer.value)) {
    return 'vgt-row-me';
  }
  if (isViewedOther(row.UserID)) return 'vgt-row-active';
  return '';
};

const formatFn = (value) => {
  return store.getters.formatTableNumber(value);
};
const onRowClick = (params) => {
  emit('result-clicked', { UserID: params.row.UserID, UserNickName: params.row.UserNickName })
  activeUserId.value = params.row.UserID;
};

const goToUserPage = (row) => {
  if (!row?.UserID) return;
  router.push({ name: 'Detail her hráče', params: { uid: row.UserID } });
};

const columns = reactive([
  { 
    label: 'Hráč',
    field: 'UserNickName',
    thClass: 'text-uppercase text-dark text-xxs font-weight-bolder',
    tdClass: 'text-sm font-weight-bold',
  },
  { 
    label: 'Pořadí',
    field: 'Rank',
    type: 'number',
    thClass: 'text-uppercase text-dark text-xxs font-weight-bolder text-md-center',
    tdClass: 'text-sm font-weight-bold text-md-center',
  },
  { 
    label: 'Hodnota portfolia',
    field: 'PortfolioAmount',
    type: 'number',
    thClass: 'text-uppercase text-dark text-xxs font-weight-bolder text-end',
    tdClass: 'text-sm font-weight-bold text-end',
    formatFn: formatFn
  },
  { 
    label: 'Výhra',
    field: 'Reward',
    thClass: 'text-uppercase text-dark text-xxs font-weight-bolder text-end',
    tdClass: 'text-sm font-weight-bold text-end',
  },
]);
</script>

<style scoped>
.user-info-btn {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  margin-right: 8px;
  padding: 0;
  border-radius: 50%;
  border: 1.5px solid #94A3B8;
  background: transparent;
  color: #94A3B8;
  font-size: 0.68rem;
  font-weight: 800;
  font-style: italic;
  font-family: Georgia, 'Times New Roman', serif;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.user-info-btn:hover {
  border-color: #FF8A4C;
  color: #FF8A4C;
}

.text-player-viewed {
  color: #5B82AC !important;
  font-weight: 700;
}
</style>